<script lang="ts" setup>
import { TYPE_DIVIDE } from '~/constants/app'
import { DESKTOP_SIDEBAR_ITEMS } from '~/resources/common'
import type { SidebarItem } from '~/types/app'
import { useScrollToTopWinner } from '~/composables/top-winner/use-scroll-to-top-winner'
import { useCasinoProvider } from '~/composables/game/use-casino-provider'
import type { CasinoProviderGame } from '~/types/game'
import { RoutesEnum } from '~/enums/routes'

const route = useRoute()
const timeout = ref()
const { onScrollToTopWinner } = useScrollToTopWinner()

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
})
const { casinoProviders } = useCasinoProvider()

onBeforeUnmount(() => {
  if (timeout.value) {
    clearTimeout(timeout.value)
  }
})

const drawer = ref(true)
const emit = defineEmits(['update:modelValue'])

const casinoMenuProviders = computed(() =>
  casinoProviders.value.map((item: CasinoProviderGame) => ({
    title: item?.name ?? '',
    key: `${RoutesEnum.LiveCasino}/${item.slug}`,
    to: `${RoutesEnum.LiveCasino}/${item.slug}`,
  })),
)

const isCollapsed = computed({
  get() {
    return props.modelValue
  },
  set(value) {
    emit('update:modelValue', value)
  },
})

const onSearchClick = () => {
  isCollapsed.value = false
  const input = document.querySelector(
    '.desktop-sidebar .form-input input',
  ) as HTMLInputElement
  if (input) {
    if (timeout.value) {
      clearTimeout(timeout.value)
    }
    timeout.value = setTimeout(() => {
      input.focus()
    }, 100)
  }
}
const { BRAND_NAME, EVENT_ENABLE } = useRuntimeConfig().public
const { navigate } = useNavigate()

const redirectMenu = (menu: SidebarItem): void => {
  navigate(menu.to, menu.loginRequired, false)
}

const menuSideBarItems = computed(() => {
  return DESKTOP_SIDEBAR_ITEMS(BRAND_NAME).map((item) => {
    if (item.key === RoutesEnum.LiveCasino) {
      return {
        ...item,
        items: casinoMenuProviders.value,
      }
    }
    return item
  })
})
</script>
<template>
  <VCard class="desktop-sidebar">
    <VLayout>
      <VNavigationDrawer
        v-model="drawer"
        :rail="isCollapsed"
        permanent
        width="282"
      >
        <VList density="compact" nav aria-label="sidebar">
          <BaseTooltip
            v-if="isCollapsed"
            text="Tìm kiếm"
            @click="onSearchClick"
          >
            <div class="search-collapsed">
              <i class="icon-search-game" />
            </div>
          </BaseTooltip>
          <DesktopCommonSidebarSearch :is-collapsed="isCollapsed" />

          <NuxtLink v-if="EVENT_ENABLE" :to="RoutesEnum.EventDrawingLots">
            <BaseTooltip v-if="isCollapsed" text="Săn siêu hũ" :is-image="true">
              <VListItem />
            </BaseTooltip>
            <img
              v-else
              class="img-entry-event"
              src="/images/event-drawing-lots/img-san-sieu-hu.webp"
              alt="entry event"
            />
          </NuxtLink>
          <div v-else @click="onScrollToTopWinner">
            <BaseTooltip v-if="isCollapsed" text="Top Winner" :is-image="true">
              <VListItem />
            </BaseTooltip>
            <img
              v-show="!isCollapsed"
              class="img-entry-event"
              src="/images/top-winning/img-san-sieu-hu.webp"
              alt="entry event"
              loading="lazy"
            />
          </div>

          <template v-for="item in menuSideBarItems" :key="item.key">
            <template v-if="item.items?.length">
              <NuxtLink v-if="isCollapsed" :to="item.to">
                <BaseTooltip :text="item.title!">
                  <VListItem
                    :prepend-icon="item.icon"
                    title=""
                    :value="item.key"
                    :class="{
                      'v-list-item--selected': route.fullPath.includes(
                        item.key,
                      ),
                    }"
                  />
                </BaseTooltip>
              </NuxtLink>
              <NuxtLink v-else :to="isCollapsed ? '' : item.to">
                <DesktopCommonSidebarMenu
                  :is-collapsed="isCollapsed"
                  :item="item"
                />
              </NuxtLink>
            </template>
            <VDivider v-else-if="item.type === TYPE_DIVIDE" />
            <BaseTooltip
              v-else
              :is-show="isCollapsed"
              :text="item.title!"
              :role="isCollapsed ? 'option' : undefined"
            >
              <a
                v-if="item?.loginRequired"
                class="v-list-item__link"
                :href="item.to"
                :aria-label="item.title"
                @click.prevent="redirectMenu(item)"
              >
                <VListItem
                  :class="{
                    'v-list-item--selected': route.path === item.key,
                  }"
                  :prepend-icon="item.icon"
                  :title="isCollapsed ? '' : item.title"
                  :value="item.key"
                />
              </a>
              <NuxtLink
                v-else
                class="v-list-item__link"
                :to="item.to!"
                :aria-label="item.title"
              >
                <VListItem
                  :class="{
                    'v-list-item--selected':
                      route.path.slice(1) &&
                      route.path.slice(1).includes(item.key.slice(1)),
                  }"
                  :prepend-icon="item.icon"
                  :title="isCollapsed ? '' : item.title"
                  :value="item.key"
                />
              </NuxtLink>
            </BaseTooltip>
          </template>
        </VList>
      </VNavigationDrawer>
    </VLayout>
  </VCard>
</template>
